import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type {
  GetFormAnswersResponse,
  PrepareAnswersExportResponse,
} from 'shared/api/requests/forms/schemas'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import { fetchAnswers, fetchFormQuestions, fetchNextAnswers, prepareExportAnswers } from './thunks'

export type ExportStatus = PrepareAnswersExportResponse['async_status']
type FormItem = NonNullable<GetFormAnswersResponse['form']>

export interface FetchArgs {
  urlParams: { id: number | string }
}

export interface AnswersState {
  items: GetFormAnswersResponse['answers'] | null
  fetching: boolean
  limiter: number | null
  listEarlyLimiter: number | null
  fetchRequestId: string | null
  fetchArgs: FetchArgs | null
  isExportingData: boolean
  status: ExportStatus | null
  formItem: FormItem | null
  isFormFetching: boolean
  formError: unknown
}

const initialExportStatus: ExportStatus = {
  done: false,
  progress: 0,
  result: null,
  signature: '',
}

type ListState = Pick<
  AnswersState,
  'items' | 'fetching' | 'limiter' | 'listEarlyLimiter' | 'fetchRequestId' | 'fetchArgs'
>

const listInitialState: ListState = {
  items: null,
  fetching: false,
  limiter: null,
  listEarlyLimiter: null,
  fetchRequestId: null,
  fetchArgs: null,
}

const initialState: AnswersState = {
  ...listInitialState,
  isExportingData: false,
  status: null,
  formItem: null,
  isFormFetching: false,
  formError: null,
}

export const answersSlice = createSlice({
  name: 'answers',
  initialState,
  reducers: {
    dropAnswers: (state) => {
      Object.assign(state, listInitialState)
    },
    updateExportingStatus: (state, action: PayloadAction<{ status: ExportStatus }>) => {
      state.isExportingData = false
      state.status = action.payload.status
    },
    resetExportingStatus: (state) => {
      state.isExportingData = false
      state.status = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('APP_UPDATE_CURRENT_ACCOUNT_ID', () => initialState)

      .addCase(fetchAnswers.pending, (state, action) => {
        if (action.meta.arg !== state.fetchArgs?.urlParams.id) {
          state.items = null
          state.limiter = null
        }
        state.fetching = true
        state.fetchRequestId = action.meta.requestId
        state.fetchArgs = { urlParams: { id: action.meta.arg } }
      })
      .addCase(fetchAnswers.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.fetchRequestId) return
        state.fetching = false
        state.items = action.payload.items
        state.limiter = action.payload.limiter
        state.listEarlyLimiter = action.payload.limiter
      })
      .addCase(fetchAnswers.rejected, (state) => {
        state.fetching = false
      })

      .addCase(fetchNextAnswers.pending, (state, action) => {
        state.fetching = true
        state.fetchRequestId = action.meta.requestId
      })
      .addCase(fetchNextAnswers.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.fetchRequestId) return
        if (state.items != null) {
          state.items = [...state.items, ...action.payload.items]
        }
        state.limiter = action.payload.limiter
        state.listEarlyLimiter = action.payload.limiter
        state.fetching = false
      })
      .addCase(fetchNextAnswers.rejected, (state) => {
        state.fetching = false
      })

      .addCase(prepareExportAnswers.pending, (state) => {
        state.isExportingData = true
        state.status = initialExportStatus
      })
      .addCase(prepareExportAnswers.fulfilled, (state, action) => {
        if (state.status?.done) return
        state.isExportingData = false
        state.status = action.payload ?? null
      })
      .addCase(prepareExportAnswers.rejected, (state) => {
        state.isExportingData = false
      })

      .addCase(fetchFormQuestions.pending, (state) => {
        state.isFormFetching = true
        state.formError = null
      })
      .addCase(fetchFormQuestions.fulfilled, (state, action) => {
        const { item, answers, questions } = action.payload
        state.formItem = item ? { ...item, answers, questions } : null
        state.isFormFetching = false
      })
      .addCase(fetchFormQuestions.rejected, (state, action) => {
        state.isFormFetching = false
        state.formError = action.payload
      })
  },
})

export const { dropAnswers, updateExportingStatus, resetExportingStatus } = answersSlice.actions

export const answersReducer = answersSlice.reducer

anotherTabNotificationsListener.on('form_answers_export_status', (data, dispatch) => {
  dispatch(updateExportingStatus(data))
})
